import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

import SteamSignInButton from "../../../auth/SteamSignInButton";
import { getPublicSiteSx } from "../../../public-site/publicSiteStyles";

export default function MinimalPublicPage({
  eyebrow,
  title,
  description,
  points,
  ctaLabel = "Join with Steam",
  secondaryLabel = "Back to homepage",
  secondaryTo = "/",
}) {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);

  return (
    <Box sx={sx.pageRoot}>
      <Container maxWidth="xl">
        <Box
          sx={{
            minHeight: { xs: "72vh", md: "68vh" },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 0.95fr) minmax(280px, 0.55fr)" },
            gap: { xs: 4, md: 8 },
            alignItems: "center",
            py: { xs: 8, md: 12 },
          }}
        >
          <Box>
            <Typography
              sx={{
                mb: 2,
                color: "#E8A33D",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              {eyebrow}
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.2rem", md: "4.4rem" },
                lineHeight: 0.98,
                fontWeight: 800,
                letterSpacing: "-0.03em",
                maxWidth: "12ch",
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                mt: 2.5,
                maxWidth: "48ch",
                color: "rgba(255,255,255,0.72)",
                fontSize: { xs: "1rem", md: "1.15rem" },
                lineHeight: 1.65,
              }}
            >
              {description}
            </Typography>
            <Box sx={{ mt: 4, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <SteamSignInButton
                label={ctaLabel}
                showAssurance={false}
                surface="hero"
                sx={sx.steamButton}
              />
              <Button
                component={Link}
                to={secondaryTo}
                variant="outlined"
                sx={sx.secondaryButton}
              >
                {secondaryLabel}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              border: "1px solid rgba(255,255,255,0.09)",
              borderRadius: "10px",
              backgroundColor: "rgba(255,255,255,0.035)",
              p: { xs: 2.5, md: 3 },
            }}
          >
            {points.map((point) => (
              <Box
                key={point.title}
                sx={{
                  py: 2,
                  borderBottom: "1px solid rgba(255,255,255,0.07)",
                  "&:last-of-type": { borderBottom: "none" },
                }}
              >
                <Typography
                  sx={{
                    color: "#ffffff",
                    fontWeight: 800,
                    fontSize: "0.98rem",
                  }}
                >
                  {point.title}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.75,
                    color: "rgba(255,255,255,0.58)",
                    fontSize: "0.9rem",
                    lineHeight: 1.55,
                  }}
                >
                  {point.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

MinimalPublicPage.propTypes = {
  eyebrow: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  points: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
    })
  ).isRequired,
  ctaLabel: PropTypes.string,
  secondaryLabel: PropTypes.string,
  secondaryTo: PropTypes.string,
};
